import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import { INQUIRY_STATUS_OPTIONS } from '../../utils/constants';
import { dropDownStyles } from '../../utils/helpers';
import AppButton from '../CustomButton';

type StatusOption = { value: string; label: string };

// Render the menu in a portal so it isn't clipped by the dialog and stays above
// MUI's Dialog (zIndex 1300).
const selectStyles = {
    ...dropDownStyles,
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

interface StatusUpdateModalProps {
    open: boolean;
    currentStatus?: string;
    onClose: () => void;
    onSubmit: (status: string) => Promise<void> | void;
}

export default function StatusUpdateModal({ open, currentStatus, onClose, onSubmit }: StatusUpdateModalProps): React.JSX.Element {
    const [selected, setSelected] = useState<StatusOption | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setSelected(INQUIRY_STATUS_OPTIONS.find((option) => option.value === currentStatus) ?? null);
            setSubmitting(false);
        }
    }, [open, currentStatus]);

    const handleSubmit = async () => {
        if (!selected) return;
        setSubmitting(true);
        try {
            await onSubmit(selected.value);
            onClose();
        } catch (error) {
            console.error('Failed to update status', error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                <Typography variant="h4">Update Status</Typography>
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 12, top: 12, color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <FormControl fullWidth>
                    <Typography sx={{ fontSize: 16, color: '#000', mb: 0.5 }}>Status*</Typography>
                    <Select
                        options={INQUIRY_STATUS_OPTIONS}
                        value={selected}
                        onChange={(option) => setSelected(option as StatusOption | null)}
                        placeholder="Select Status"
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                    />
                </FormControl>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <AppButton
                    variant="contained"
                    className="sm"
                    sx={{ mr: 1.5 }}
                    onClick={handleSubmit}
                    disabled={!selected || submitting}
                >
                    {submitting ? 'Updating...' : 'Update'}
                </AppButton>
                <AppButton variant="outlined" className="sm" onClick={onClose} disabled={submitting}>
                    Cancel
                </AppButton>
            </DialogActions>
        </Dialog>
    );
}
